import { initApp } from '@adornis/functional-lit/init-app.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { routingStateSignal } from '@adornis/router/client/routing-state-signal.js';
import { LandingEditorPage } from './pages/editor.js';
import { Home } from './pages/home.js';
import { StaticPage } from './pages/staticPage.js';
import { prepareClientRoot } from './prepareClientRoot.js';
import { installLandingDocumentNavigation } from './state/landingDocumentNavigation.js';
import 'flag-icons/css/flag-icons.min.css';
import './fonts.css';
import './tailwind.css' with { type: 'tailwind' };
import './styles/company-matching-discovery.css';
import { publicIndustryPageKeys } from '../shared/content/industryPages.js';

installLandingDocumentNavigation();

const router = makeRouter(
  {
    ...Object.fromEntries(publicIndustryPageKeys.map(key => [key, {
      path: `/branchen/${key}`,
      render: () => StaticPage(key),
    }])),
    home: {
      path: '/',
      render: () => Home(),
    },
    application: {
      path: '/bewerben',
      render: () => StaticPage('bewerben'),
    },
    company: {
      path: '/unternehmen',
      render: () => StaticPage('unternehmen'),
    },
    globus: {
      path: '/globus',
      render: () => StaticPage('globus'),
    },
    process: {
      path: '/ablauf',
      render: () => StaticPage('ablauf'),
    },
    about: {
      path: '/ueber-vivaporta',
      render: () => StaticPage('ueber-vivaporta'),
    },
    team: {
      path: '/team',
      render: () => StaticPage('team'),
    },
    contact: {
      path: '/kontakt',
      render: () => StaticPage('kontakt'),
    },
    editor: {
      path: '/editor',
      render: () => LandingEditorPage(),
    },
    imprint: {
      path: '/impressum',
      render: () => StaticPage('impressum'),
    },
  },
  { fallback: () => '404' },
);
router.init();

prepareClientRoot(document.getElementById('root'));

// Client-side routing keeps the previous scroll offset when the outlet swaps pages, so opening a new
// page would land mid-scroll. Reset to the top whenever the path changes. In-page anchor navigations
// (hash present) keep their target position and are left untouched.
// The page scrolls inside the `.vp-page` container (not the window), and that element is reused across
// route changes — so we reset its scrollTop. `instant` overrides the container's `scroll-behavior: smooth`
// so the jump is immediate, and we run it after the new page renders via requestAnimationFrame.
const scrollActivePageToTop = () => {
  const page = document.querySelector<HTMLElement>('.vp-page');
  if (page) page.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
};

let lastRoutedPath = routingStateSignal.value.path;
routingStateSignal.subscribe(state => {
  if (state.path === lastRoutedPath) return;
  lastRoutedPath = state.path;
  if (state.hash) return;
  scrollActivePageToTop();
  requestAnimationFrame(scrollActivePageToTop);
});

initApp(() => router.outlet.value);
